import { DatePipe } from '@angular/common';
import {
  ColumnDef,
  flexRenderComponent,
} from '@3ddv/software-division-components/backoffice/table';
import { gsActionsColumn } from '../../shared/ui/table/gs-table.columns';
import { Event as SaleEvent } from './models/event.model';
import { GsEventAttachCellComponent } from './cells/gs-event-attach-cell.component';
import { GsEventSelectionCellComponent } from './cells/gs-event-selection-cell.component';
import { GsEventSelectionHeadComponent } from './cells/gs-event-selection-head.component';

export type EventAttachmentKind = 'adlink' | 'upsell' | 'upgrade';

export interface EventColumnsConfig {
  datePipe: DatePipe;
  isSelected: (event: SaleEvent) => boolean;
  isAllPageSelected: () => boolean;
  isSomePageSelected: () => boolean;
  onToggleEventSelected: (event: SaleEvent, selected: boolean) => void;
  onToggleAllOnPageSelected: (selected: boolean) => void;
  onOpenDetails: (event: SaleEvent) => void;
  onToggleEnabled: (event: SaleEvent) => void;
  onDelete: (event: SaleEvent) => void;
  onAttachmentEdit: (event: SaleEvent, kind: EventAttachmentKind) => void;
  canEdit?: boolean;
}

export function buildEventColumns(
  config: EventColumnsConfig
): ColumnDef<SaleEvent>[] {
  const canEdit = config.canEdit ?? true;

  const selectionColumn: ColumnDef<SaleEvent> = {
    id: 'selection',
    header: () =>
      flexRenderComponent(GsEventSelectionHeadComponent, {
        inputs: {
          checked: config.isAllPageSelected(),
          indeterminate: config.isSomePageSelected(),
        },
        outputs: {
          toggled: (selected: boolean) =>
            config.onToggleAllOnPageSelected(selected),
        },
      }),
    cell: context => {
      const event = context.row.original;

      return flexRenderComponent(GsEventSelectionCellComponent, {
        inputs: {
          checked: config.isSelected(event),
        },
        outputs: {
          toggled: (selected: boolean) =>
            config.onToggleEventSelected(event, selected),
        },
      });
    },
    enableSorting: false,
  };

  const codeColumn: ColumnDef<SaleEvent> = {
    id: 'code',
    header: 'Code',
    accessorFn: event => event.code,
  };

  const descriptionColumn: ColumnDef<SaleEvent> = {
    id: 'description',
    header: 'Description',
    accessorFn: event => event.description,
  };

  const dateColumn: ColumnDef<SaleEvent> = {
    id: 'date',
    header: 'Date',
    accessorFn: event => config.datePipe.transform(event.date) ?? '',
  };

  const attachmentColumn = (
    id: string,
    header: string,
    countOf: (event: SaleEvent) => number,
    kind: EventAttachmentKind,
    titleFor: (count: number) => string,
    iconFor: (count: number) => 'pencil' | 'plus'
  ): ColumnDef<SaleEvent> => ({
    id,
    header,
    cell: context => {
      const event = context.row.original;
      const count = countOf(event);

      return flexRenderComponent(GsEventAttachCellComponent, {
        inputs: {
          count,
          icon: iconFor(count),
          title: titleFor(count),
          editable: canEdit,
        },
        outputs: {
          clicked: () => config.onAttachmentEdit(event, kind),
        },
      });
    },
    enableSorting: false,
  });

  const adlinkColumn = attachmentColumn(
    'adlink',
    'AdLink',
    event => event.adlinks?.length ?? 0,
    'adlink',
    count => (count ? 'Edit Adlinks' : 'Add Adlinks'),
    count => (count ? 'pencil' : 'plus')
  );

  const upsellsColumn = attachmentColumn(
    'upsells',
    'Upsells',
    event => event.upsells?.length ?? 0,
    'upsell',
    () => 'Edit Upsells',
    () => 'pencil'
  );

  const upgradesColumn = attachmentColumn(
    'upgrade',
    'Upgrade',
    event => event.upgrades?.length ?? 0,
    'upgrade',
    () => 'Edit Upgrade',
    () => 'pencil'
  );

  const actionsColumn = gsActionsColumn<SaleEvent>({
    actions: [
      {
        id: 'details',
        label: 'Details',
        dataCy: event => `event-details-button-${event.id}`,
      },
      ...(canEdit
        ? [
            {
              id: 'toggle',
              label: event => (event.isEnabled ? 'Disable' : 'Enable'),
              tone: event => (event.isEnabled ? 'negative' : 'positive'),
              dataCy: event => `event-enable-button-${event.id}`,
            },
            {
              id: 'delete',
              label: 'Delete',
              destructive: true,
              dataCy: event => `event-delete-button-${event.id}`,
            },
          ]
        : []),
    ],
    onSelect: (actionId, event) => {
      if (actionId === 'details') {
        config.onOpenDetails(event);
      } else if (actionId === 'toggle') {
        config.onToggleEnabled(event);
      } else if (actionId === 'delete') {
        config.onDelete(event);
      }
    },
    rowDataCy: event => `event-row-${event.id}`,
  });

  return [
    selectionColumn,
    codeColumn,
    descriptionColumn,
    dateColumn,
    adlinkColumn,
    upsellsColumn,
    upgradesColumn,
    actionsColumn,
  ];
}
